import {
  Box,
  CircularProgress,
  Drawer,
  Grid,
  IconButton,
  Modal,
  Popover,
  SwipeableDrawer,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';

import ArrowIcon from 'common/shared-ui/src/icons/ArrowIcon';
import palette from 'common/shared-ui/src/theme/palette';

import {
  getAgileSavedQueriesByProject,
  getConfirmationsSavedQueries,
  getIssuesSavedQueries,
  getMenuNavigation,
  getMenuNavigationId,
  getTimesheetSavedQueries,
} from '../../../../packages/common/api';
import ChevronIcon from '../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import ClearIcon from '../../../../packages/common/shared-ui/src/icons/ClearIcon';
import { generateRedirectUrl } from '../../../../packages/common/utils/getRedirectUrl';
import { getTranslatedText } from '../../../../packages/common/utils/getTranslatedText';
import AppLayout from '../../app/AppLayout';
import { store } from '../../app/redux';
import { toggleDrawer } from '../../app/redux/reducers/userSlice';
import MenuItem from '../MenuItem';

// Original class: drawerModal
const StyledDrawerModal = styled('div')(({ theme }) => ({
  zIndex: '1999 !important',
  visibility: 'visible !important',
  maxWidth: 305,

  '&.MuiModal-hidden': {
    maxWidth: 36,
  },

  '& .MuiBackdrop-root': {
    opacity: '15% !important',
    backgroundColor: '#212346',
  },

  //[theme.breakpoints.down('sm')]: {
  //  top: '80px !important',
  //  zIndex: '2000 !important',
  //  '&.MuiModal-hidden': {
  //    maxWidth: 0,
  //  },
  //},
}));
// Original class: drawerPaper
const StyledDrawerPaper = styled(Drawer, {
  shouldForwardProp: prop => !['openStyle'].includes(prop),
})(({ theme, openStyle }) => ({
  '& .MuiButtonBase-root.MuiIconButton-root': {
    margin: '32px auto 0 auto',
    height: '24px',
    width: '24px',
    [theme.breakpoints.down('sm')]: {
      margin: '0 8px 0 auto',
    },
    ...(openStyle && {
      margin: '32px 8px 0 auto',
    }),
  },
  '& .MuiPaper-root': {
    minWidth: 36,
    boxShadow: 'unset !important',
    marginTop: theme.spacing(15),
    transform: 'unset !important',
    visibility: 'visible !important',
    pointerEvents: 'all !important',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    ...(openStyle && {
      minWidth: 305,
      maxWidth: 305,
      overflowY: 'scroll',
      left: 0,
      zIndex: 1999,
      paddingTop: '38px',
      marginTop: theme.spacing(15),
      transform: 'unset !important',
      boxShadow:
        '0px 8px 10px -5px rgb(0 0 0 / 20%), 0px 16px 24px 2px rgb(0 0 0 / 14%), 0px 6px 30px 5px rgb(0 0 0 / 12%)',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    }),
  },
  [theme.breakpoints.down('sm')]: {
    backgroundColor: 'transparent !important',
    minWidth: 250,
    height: 'auto!important',
    ...(openStyle && {
      backgroundColor: '#fff !important',
    }),
  },
}));
// Original class: mobileMenuUserInfo
const StyledMobileMenuUserInfo = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(5, 5, 2.5, 5),
  backgroundColor: 'rgba(0, 0, 0, 0.25)',

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'row!important',
  },
}));
// Original class: avatar
const StyledAvatar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 64,
  height: 64,
  borderRadius: '50%',
  overflow: 'hidden',
  backgroundColor: '#37C670',

  [theme.breakpoints.down('sm')]: {
    width: 30,
    height: 30,
    marginBottom: '0!important',
  },
}));
// Original class: drawerMenu
const StyledDrawerMenu = styled(Grid)(({ theme }) => ({
  marginTop: '8px',
  transition: 'all .1s ease',

  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(2.5),
  },
}));
// Original class: spinner
const StyledSpinner = styled(Grid)(theme => ({
  backgroundColor: 'rgba(31, 33, 72, 0.3)',
  zIndex: 3000,
  color: '#F1F2F8',
}));
// Original class: content
const StyledContent = styled(Grid)(({ theme }) => ({
  marginLeft: 36,
  marginTop: 60,
  overflowX: 'hidden',
  minHeight: '90vh',

  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    marginTop: 80,
  },
}));
// Original class: AppLayoutRoot
const StyledAppLayoutRoot = styled(AppLayout)(({ theme }) => ({
  margin: '20px auto 0 auto',
  padding: '0px 44px',

  [theme.breakpoints.down(1330)]: {
    // maxWidth: 944,
  },

  [theme.breakpoints.down('lg')]: {
    // maxWidth: 717,
    padding: theme.spacing(0, 5),
  },

  [theme.breakpoints.down('sm')]: {
    maxWidth: 320,
    padding: theme.spacing(0, 2.5, 2.5),
  },
}));
const LeftDrawer = ({ children }) => {
  const navigate = useNavigate();
  const isUserAdmin = useSelector(state => state.user.info.isAdmin);
  const userLastName = useSelector(state => state.user.info.lastName);
  const userFirstName = useSelector(state => state.user.info.firstName);
  const userFullNameArray = useSelector(
    state => state.user.info.fullName,
  ).split(' ');
  const userInitials = `${userFullNameArray[0][0]}${userFullNameArray[userFullNameArray.length - 1][0]}`;
  const avatar = null;
  const isSmallScreen = useMediaQuery(theme =>
    theme.breakpoints.maxWidth('sm'),
  );
  const [isSpinnerShown, setIsSpinnerShown] = useState(false);

  const drawerState = useSelector(state => state.user.isDrawerOpen);
  const [drawerProps, setDrawerProps] = useState({
    modalProps: {
      keepMounted: true,
    },
  });

  const [menuItemsGlobal, setMenuItemsGlobal] = useState([]);
  const [menuItemsContext, setMenuItemsContext] = useState([]);
  const state = store.getState();
  const pageParams = useParams();

  const handleError = useCallback(
    error => {
      if (error) {
        navigate(`/errors/error-${error.status}`);
      }
    },
    [navigate],
  );

  const dispatch = useDispatch();
  const openDrawer = useCallback(
    () => dispatch(toggleDrawer(true)),
    [dispatch],
  );
  useEffect(() => {
    let ignore = false;
    async function getMenu() {
      let response;
      if (pageParams.projectId) {
        response = await getMenuNavigationId(pageParams.projectId);
      } else {
        response = await getMenuNavigation();
      }
      if (!ignore) {
        return response.data;
      }
    }

    getMenu()
      .then(res => {
        const subItems = [
          'projectsNavItem',
          'issuesNavItem',
          'financeNavItem',
          'timesheetsNavItem',
          'confirmationsNavItem',
          'issuesKanbanNavItem',
          'projectIssuesNavItem',
          'projectConfirmationsNavItem',
          'projectReportsNavItem',
          'projectTimesheetsNavItem',
          'projectIssuesKanbanNavItem',
        ];
        let itemsGlobal = [];
        let itemsContext = [];
        if (res.global) {
          itemsGlobal = res.global.map(item => {
            let menuItem = item;
            !item.subitems &&
              subItems.includes(item.itemId) &&
              (menuItem.subitems = []);

            return menuItem;
          });
        }
        if (res.context) {
          itemsContext = res.context.map(item => {
            let menuItem = { ...item, context: true };
            !item.subitems &&
              subItems.includes(item.itemId) &&
              (menuItem.subitems = []);

            return menuItem;
          });
        }
        setMenuItemsGlobal(itemsGlobal);
        setMenuItemsContext(itemsContext);
      })
      .catch(error => handleError(error.response));
  }, [handleError, pageParams.projectId]);

  const redirectToExternalUrl = link => {
    window.open(link, '_blank');
  };
  const { current: menuItemStyles } = useRef({
    style: {
      width: 24,
      height: 24,
      color: palette.text.alt,
    },
  });
  const handleOpenDrawer = useCallback(() => {
    dispatch(toggleDrawer(false));
  }, [dispatch]);
  const handleProjectLink = useCallback(() => {
    navigate(`/projects/${state.user.project.redmineId}`);
    dispatch(toggleDrawer(false));
  }, [dispatch, navigate, state.user.project]);
  return (
    <Grid container wrap='nowrap'>
      <StyledDrawerPaper
        openStyle={drawerState}
        open={drawerState}
        onClose={handleOpenDrawer}
        slotProps={{
          root: { component: StyledDrawerModal }, // заменяем root Modal на styled-компонент
        }}
        ModalProps={drawerProps.modalProps}
      >
        {!isSmallScreen && !drawerState && (
          <IconButton onClick={openDrawer}>
            <ArrowIcon direction='left' viewBox='0 0 24 24' />
          </IconButton>
        )}
        {/*{isSmallScreen ? (
          <StyledMobileMenuUserInfo container direction='column'>
            <StyledAvatar
              sx={{
                marginBottom: 2.5,
              }}
            >
              {avatar ? (
                <img src={avatar} alt='user avatar' />
              ) : (
                <Typography
                  variant='h4'
                  color='primary'
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  {userInitials}
                </Typography>
              )}
            </StyledAvatar>
            <Grid
              container
              wrap='nowrap'
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                width: 'auto',
              }}
            >
              <Typography
                variant='h3'
                sx={{
                  fontWeight: 600,
                  color: '#E4E4EF',
                  fontSize: '14px',
                }}
              >
                {userFirstName} {userLastName}
              </Typography>
              <IconButton disableRipple>
                <ChevronIcon
                  direction='down'
                  width={24}
                  height={24}
                  viewBox='0 0 24 24'
                  color='#E4E4EF'
                />
              </IconButton>
            </Grid>
          </StyledMobileMenuUserInfo>
        ) : null}*/}
        {drawerState && (
          <StyledDrawerMenu container direction='column'>
            {menuItemsGlobal.map((item, index) => (
              <MenuItem key={index} item={item}></MenuItem>
            ))}
            {state.user.project && (
              <Box
                onClick={handleProjectLink}
                sx={{
                  backgroundColor: '#F0F1F8',
                  padding: '6px 10px',
                  marginTop: '20px',
                  marginBottom: '10px',
                  cursor: 'pointer',
                }}
              >
                <Typography
                  variant='h3'
                  sx={{
                    fontWeight: 700,
                    color: '#212346',
                  }}
                >
                  {state.user.project.name}
                </Typography>
              </Box>
            )}
            {menuItemsContext.map((item, index) => (
              <MenuItem key={index} item={item} context={true}></MenuItem>
            ))}
            {/*{menuItems.map((menuItem, itemProps) => (
              <MenuItem
                item={menuItem}
                itemName={menuItem.lang_key}
                itemIcon={menuItem.rowIcon && menuItem.rowIcon(menuItemStyles)}
                path={menuItem.path}
                key={menuItem.lang_key}
                closeDrawer={() => handleOpenDrawer(false)}
                redirectToExternalUrl={redirectToExternalUrl}
                redirectToAnotherUrl={redirectToAnotherUrl}
                {...itemProps}
                {...{ currentItem: window.location.pathname === menuItem.path }}
              />
            ))}*/}
          </StyledDrawerMenu>
        )}
      </StyledDrawerPaper>
      {process.env.NODE_ENV !== 'development' && isSpinnerShown && (
        <StyledSpinner
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100vh',
            position: 'absolute',
          }}
        >
          <CircularProgress color='inherit' />
        </StyledSpinner>
      )}
      <StyledContent container size='grow'>
        <StyledAppLayoutRoot size='grow'>{children}</StyledAppLayoutRoot>
      </StyledContent>
    </Grid>
  );
};

export default memo(LeftDrawer);
